import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useCallback } from 'react';

import { fetchGlsInit } from '@/api/gls/fetch-gls-init';
import { buildLobbyUrl } from '@/api/gls/build-lobby-url';
import { getClientIpFromGeolocationToken } from '@/api/gls/get-client-ip';
import {
  clearGlsResponseJackpotMode,
  recordGlsResponseJackpotMode,
} from '@/game-client/gls-jackpot-mode-tracker';
import { storeGlsInitializeUrl } from '@/game-client/gls-pending-initialize-url';
import {
  invalidateGlsLaunch,
  isGlsLaunchGenerationCurrent,
  releaseGlsLaunch,
  tryBeginGlsLaunch,
} from '@/game-client/gls-launch-guard';
import {
  setGameContext,
  setGameStatus,
  setGeolocationContext,
} from '@/store/actions';
import { useStore } from '@/store/useStore';
import { GeolocationStoreParts } from '@/store/slices/geolocation/types';
import { openExternal } from '@/utilities/helpers/openExternalWebView';
import {
  markLaunchVitalsReconciliationPending,
  markLaunchVitalsReconciledAttemptReady,
  processGlsDurationVital,
} from '@/utilities/logger/datadog/vitals';
import { VitalDurationType } from '@fanduel/casino-datadog-logger/src/types';
import config from '@/config';
import { GAME_CLIENT_ERRORS } from '@/utilities/constants/gameClientErrors';
import { PRODUCTS } from '@/utilities/constants/game';
import { getGeolocationCookieName } from '@/utilities/getters/cookieGetters';
import { updateGeolocationToken } from '@/api/geolocation/update-geolocation-token';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import {
  DdError,
  DdEventCategory,
  DdEventType,
} from '@/utilities/logger/datadog/types';
import { removeCookieEverywhere } from '@/utilities/helpers/removeCookieEverywhere';
import { normalizeRegion } from '@/utilities/constants/regions';
import { useAccountUm } from '@/unified-modules/account/useAccountUm';
import {
  getGameDataFromNative,
  isNativeGlsFetchSupported,
} from '@/utilities/helpers/wrapperHelpers';
import {
  ddNativeGlsEntered,
  ddNativeGlsFailed,
  ddNativeGlsMalformedFallback,
  ddNativeGlsSuccess,
} from '@/utilities/logger/datadog/rumEvents/nativeGlsActions';
import { extractDirectLaunchParamsFromGlsData } from '@/api/gls/extract-direct-launch-params-from-gls-data';
import { normalizeGlsLaunchProvider } from '@/api/gls/normalize-gls-launch-provider';
import type { GlsInitResponse } from '@/api/gls/types';

const TERMS_NOT_ACCEPTED_ERROR_CODE =
  'USER_HAS_NOT_ACCEPTED_TERMS_AND_CONDITIONS';

const redirectToTermsAndConditions = (region?: string | null) => {
  const safeRegion = normalizeRegion(region);
  if (!safeRegion) {
    Logger.logRumError({
      name: 'TERMS_REDIRECT_INVALID_REGION',
      description: '[GW][T&C] Blocked redirect for unrecognised region',
      useCase: DdEventCategory.GameLaunch,
      eventType: DdEventType.Error,
    });
    return;
  }

  const finalUrl = config.termsAndConditionsUrl
    .replace(/{region}/g, safeRegion)
    .replace(
      /{externalReferrerNext}/g,
      encodeURIComponent(window.location.search),
    );

  if (useStore.getState().runtime.wrapperType?.isXsell) {
    openExternal(finalUrl);
    return;
  }

  window.location.replace(finalUrl);
};

export type GlsLaunchParams = {
  userId: string;
  authToken: string;
  loginToken: string;
  gameUid: string;
  geolocationToken: string;
  region?: string;
  glsPlatform: string;
  jackpotMode?: boolean;
};

const GLS_FETCH_MAX_RETRIES = 2;

const isRetryableGlsNetworkError = (error: unknown): boolean =>
  isAxiosError(error) && !error.response;

const getGlsErrorCode = (error: unknown): unknown =>
  error && typeof error === 'object' && 'code' in error
    ? (error as { code?: unknown }).code
    : undefined;

/**
 * Reads the GLS payload the native app pre-fetched before the webview loaded.
 * Only the first-ever launch (`use-client-wrapper-effects`) uses this; the
 * reconcile flows always go through the web fetch, so native is never
 * intertwined with reconciliation. Returns `undefined` on every degraded case
 * so the caller falls back to `fetchGlsInit`.
 */
export const tryGetNativeGlsResponse = (): GlsInitResponse | undefined => {
  if (!isNativeGlsFetchSupported()) {
    return undefined;
  }

  ddNativeGlsEntered();

  let nativeResponse: GlsInitResponse | undefined;

  try {
    nativeResponse = getGameDataFromNative();
  } catch (error) {
    ddNativeGlsFailed({ error: (error as Error)?.message });

    return undefined;
  }

  if (!nativeResponse) {
    return undefined;
  }

  if (!nativeResponse.data?.launchDetails || !nativeResponse.data?.requestId) {
    ddNativeGlsMalformedFallback();

    return undefined;
  }

  ddNativeGlsSuccess();

  return nativeResponse;
};

export const isFgpRgi = (rgi: string | null | undefined): boolean =>
  rgi?.toLowerCase() !== 'incentive_games';

export const useGLS = () => {
  const { keepSessionAlive } = useAccountUm();
  const { mutateAsync: fetchGlsInitWithRetry } = useMutation({
    mutationFn: fetchGlsInit,
    retry: (failureCount, error) =>
      failureCount < GLS_FETCH_MAX_RETRIES && isRetryableGlsNetworkError(error),
    onError: (error) => {
      Logger.logRumError({
        name: '[GW][FETCH_GLS_INIT] Error',
        useCase: DdEventCategory.GameLaunch,
        eventType: DdEventType.Error,
        context: {
          errorCode: getGlsErrorCode(error),
          errorMessage: error instanceof Error ? error.message : undefined,
        },
      });
    },
  });

  const fetchGame = useCallback(
    async (
      params: GlsLaunchParams,
      shouldSkipPrismic = false,
      prefetchedResponse?: GlsInitResponse,
    ) => {
      const generation = tryBeginGlsLaunch();
      if (generation === null) {
        return;
      }

      clearGlsResponseJackpotMode();
      setGameStatus({ gameFetchStarted: true });
      processGlsDurationVital(VitalDurationType.START);

      try {
        const isPoker = useStore.getState().runtime.product === PRODUCTS.POKER;

        void keepSessionAlive();

        const response =
          prefetchedResponse ??
          (await fetchGlsInitWithRetry({
            userId: params.userId,
            authToken: params.authToken,
            loginToken: params.loginToken,
            gameUid: params.gameUid,
            geolocationToken: params.geolocationToken,
            lobbyUrl: buildLobbyUrl(params.region),
            ip: getClientIpFromGeolocationToken(params.geolocationToken),
            platform: params.glsPlatform,
            region: params.region,
            jackpotMode: params.jackpotMode,
            ...(isPoker ? { product: PRODUCTS.POKER.toUpperCase() } : {}),
            shouldSkipPrismic,
          }));

        if (!isGlsLaunchGenerationCurrent(generation)) {
          return;
        }

        if (response.error || !response.data?.gameData?.provider) {
          processGlsDurationVital(VitalDurationType.STOP);
          const errorCode =
            typeof response.error?.errorCode === 'string'
              ? response.error.errorCode
              : null;

          if (errorCode?.includes(TERMS_NOT_ACCEPTED_ERROR_CODE)) {
            releaseGlsLaunch(generation);
            redirectToTermsAndConditions(params.region);
            return;
          }

          const requiredProduct = response.error?.requiredProduct;
          const gameContext = useStore.getState().gameContext;
          if (
            errorCode?.includes(
              GAME_CLIENT_ERRORS.GEOLOCATION_PRODUCT_MISMATCH,
            ) &&
            (requiredProduct === 'CASINO' || requiredProduct === 'POKER') &&
            !gameContext.geoProductRetryAttempted
          ) {
            if (
              useStore.getState().geolocationContext.geolocationToken ===
              params.geolocationToken
            ) {
              removeCookieEverywhere(getGeolocationCookieName());
            }

            releaseGlsLaunch(generation);
            setGameContext({
              launchGeoProduct: requiredProduct,
              geoProductRetryAttempted: true,
            });
            setGameStatus({
              gameFetchStarted: false,
              gameFetchFailed: false,
              gameFetchErrorCode: null,
            });
            setGeolocationContext({ geolocationToken: null });
            useStore
              .getState()
              .resetGeolocationPart(GeolocationStoreParts.geolocationStatus);
            return;
          }

          if (errorCode === GAME_CLIENT_ERRORS.NO_VALID_GEO_APPROVAL) {
            if (
              useStore.getState().geolocationContext.geolocationToken ===
              params.geolocationToken
            ) {
              removeCookieEverywhere(getGeolocationCookieName());
              setGeolocationContext({ geolocationToken: null });
              useStore
                .getState()
                .resetGeolocationPart(GeolocationStoreParts.geolocationStatus);
            }
          }

          setGameStatus({
            gameFetchFailed: true,
            gameFetchErrorCode: errorCode,
          });
          releaseGlsLaunch(generation);
          return;
        }

        const gamingSessionTrackingId = response.data?.gamingSessionTrackingId;
        const gameStudio = response.data?.gameData?.gameStudio;
        setGameContext({
          glsRequestId: response.data?.requestId ?? null,
          ...(gamingSessionTrackingId && { gamingSessionTrackingId }),
          ...(gameStudio && { gameStudio }),
        });

        const glsResponseJackpotMode = response.data.gameData.jackpotMode;
        recordGlsResponseJackpotMode(glsResponseJackpotMode);
        if (glsResponseJackpotMode === true) {
          markLaunchVitalsReconciliationPending();
        } else {
          markLaunchVitalsReconciledAttemptReady();
        }

        const glsProvider = response.data?.gameData?.provider;
        const providerUid = response.data?.gameData?.providerUid;
        const providerRef = response.data?.gameData?.providerRef;

        if (glsProvider || providerUid || providerRef) {
          const launchProvider = glsProvider
            ? normalizeGlsLaunchProvider(glsProvider.toUpperCase())
            : undefined;
          setGameContext({
            ...(launchProvider && {
              provider: launchProvider,
              glsLaunchProvider: launchProvider,
            }),
            ...(providerUid && { providerUid }),
            ...(providerRef && { providerRef }),
          });
        }

        const directLaunchParams = extractDirectLaunchParamsFromGlsData(
          response.data,
        );
        if (directLaunchParams) {
          setGameContext({ directLaunchParams });
        }

        setGameStatus({
          gameFetchSuccess: true,
          gameLoadStarted: true,
        });

        const latestToken =
          useStore.getState().geolocationContext.geolocationToken;

        const initializeUrlPayload = {
          ...response,
          brand: useStore.getState().runtime.brand,
          userId: params.userId,
        };
        storeGlsInitializeUrl(initializeUrlPayload, generation);

        if (latestToken && latestToken !== params.geolocationToken) {
          updateGeolocationToken(latestToken).catch((error) => {
            Logger.logRumError(error as DdError);
          });
        }
      } catch {
        if (!isGlsLaunchGenerationCurrent(generation)) {
          return;
        }

        processGlsDurationVital(VitalDurationType.STOP);
        setGameStatus({ gameFetchFailed: true });
        releaseGlsLaunch(generation);
      }
    },
    [fetchGlsInitWithRetry, keepSessionAlive],
  );

  const resetGlsLaunch = useCallback(() => {
    invalidateGlsLaunch();
  }, []);

  return { fetchGame, resetGlsLaunch };
};
